import React, { useState } from 'react'
import './OpeningTree.scss'
import OpeningSearch from './components/OpeningSearch'
import OpeningTreeNode from './components/OpeningTreeNode'
import CopyChip from './components/CopyChip'
import { toPgn } from './pgn'
import { lineKey, ROOT_FEN, useOpeningTree } from './useOpeningTree'

const OpeningTree = () => {
  const {
    childrenByLine,
    rootFen,
    rootLine,
    expandedLines,
    loadingLines,
    selectedFen,
    selectedLine,
    rootLoaded,
    error,
    loadChildren,
    toggleLine,
    revealLine,
    rerootAt,
    resetRoot,
    collapseAll,
    selectNode
  } = useOpeningTree()

  const [namedOnly, setNamedOnly] = useState(false)

  const allRootChildren = childrenByLine[lineKey(rootLine)]
  const rootChildren = namedOnly
    ? allRootChildren?.filter(child => child.openingName !== null)
    : allRootChildren
  const isRerooted = rootFen !== ROOT_FEN

  return (
    <div className="otree-page">
      <div className="otree-header">
        <div>
          <h3 className="otree-title">Opening Tree Explorer</h3>
          {rootLoaded && allRootChildren && (
            <span className="otree-count">
              {rootChildren?.length ?? 0}
              {namedOnly ? ' named ' : ' '}
              {isRerooted ? 'continuations' : 'first moves'}
              {namedOnly && ` of ${allRootChildren.length}`}
            </span>
          )}
        </div>
        <OpeningSearch
          onRevealLine={revealLine}
          onJumpToPosition={(fen, line) => rerootAt(fen, line)}
        />
      </div>

      <div className="otree-toolbar">
        <label className="otree-switch">
          <input
            type="checkbox"
            checked={namedOnly}
            onChange={event => setNamedOnly(event.target.checked)}
          />
          <span>Named openings only</span>
        </label>
        <button className="otree-toolbar-btn" onClick={collapseAll}>
          Collapse all
        </button>
        {isRerooted && (
          <button className="otree-toolbar-btn is-primary" onClick={resetRoot}>
            Back to the starting position
          </button>
        )}
      </div>

      {selectedFen !== null && (
        <div className="otree-breadcrumb">
          <div className="otree-breadcrumb-line">
            <span className="otree-breadcrumb-label">Line</span>
            <span className="otree-breadcrumb-moves">
              {selectedLine.length === 0 ? 'starting position' : toPgn(selectedLine)}
            </span>
          </div>
          <div className="otree-breadcrumb-chips">
            <CopyChip label="PGN" value={toPgn(selectedLine)} />
            <CopyChip label="FEN" value={selectedFen} />
          </div>
        </div>
      )}

      <div className="otree-card">
        {error && <div className="otree-error">{error}</div>}

        {!rootLoaded && (
          <div className="otree-loading">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="otree-skel-row">
                <div className="otree-skel-line w40" />
                <div className="otree-skel-line w25" />
              </div>
            ))}
          </div>
        )}

        {rootLoaded && !rootChildren?.length && !error && (
          <div className="otree-empty">
            <span>♟</span>
            <p>
              {namedOnly
                ? 'No named openings at this level'
                : isRerooted
                  ? 'No continuations from here'
                  : 'The Opening Tree is empty'}
            </p>
          </div>
        )}

        {rootChildren?.length ? (
          <div className="otree-tree">
            {rootChildren.map(node => (
              <OpeningTreeNode
                key={node.san}
                node={node}
                ply={rootLine.length + 1}
                depth={0}
                line={[...rootLine, node.san]}
                childrenByLine={childrenByLine}
                expandedLines={expandedLines}
                loadingLines={loadingLines}
                selectedLine={selectedLine}
                selectedFen={selectedFen}
                namedOnly={namedOnly}
                onToggle={toggleLine}
                onSelect={selectNode}
                onRetry={loadChildren}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default OpeningTree
